import React, { useEffect, useRef, useState } from 'react';
import { Button, Dropdown, DropdownItem } from 'common/components';
import { ChevronUp, ChevronDown, ListFilter } from 'lucide-react';
import styles from './Table.module.scss';

function groupOptionsByCategory(
    options: TableHeaderFilterOption[]
): Array<{ category: string | null; options: TableHeaderFilterOption[] }> {
    const groups = new Map<string | null, TableHeaderFilterOption[]>();
    for (const opt of options) {
        const key = opt.category?.trim() || null;
        let bucket = groups.get(key);
        if (!bucket) {
            bucket = [];
            groups.set(key, bucket);
        }
        bucket.push(opt);
    }
    return Array.from(groups, ([category, opts]) => ({ category, options: opts }));
}

export type TableHeaderFilterOption = { value: string; label: string; category?: string };

export type TableHeaderCellSortConfig = {
    isSorted: boolean;
    direction: 'asc' | 'desc';
    onSort: () => void;
};

type TableHeaderCellFilterOptionsBase = {
    options: TableHeaderFilterOption[];
    allOptionLabel?: string;
};

export type TableHeaderCellSingleSelectFilterConfig = TableHeaderCellFilterOptionsBase & {
    mode?: 'single';
    selectedValue: string;
    onChange: (value: string) => void;
};

export type TableHeaderCellMultiSelectFilterConfig = TableHeaderCellFilterOptionsBase & {
    mode: 'multi';
    selectedValues: string[];
    onChange: (values: string[]) => void;
};

export type TableHeaderCellFilterOptionsConfig =
    | TableHeaderCellSingleSelectFilterConfig
    | TableHeaderCellMultiSelectFilterConfig;

function isMultiSelectFilter(
    config: TableHeaderCellFilterOptionsConfig
): config is TableHeaderCellMultiSelectFilterConfig {
    return config.mode === 'multi';
}

function isFilterOptionSelected(config: TableHeaderCellFilterOptionsConfig, value: string): boolean {
    if (isMultiSelectFilter(config)) {
        return value === '' ? config.selectedValues.length === 0 : config.selectedValues.includes(value);
    }
    return config.selectedValue === value;
}

function handleFilterOptionChange(config: TableHeaderCellFilterOptionsConfig, value: string): void {
    if (isMultiSelectFilter(config)) {
        if (value === '') {
            config.onChange([]);
            return;
        }
        const next = config.selectedValues.includes(value)
            ? config.selectedValues.filter((v) => v !== value)
            : [...config.selectedValues, value];
        config.onChange(next);
        return;
    }
    config.onChange(value);
}

function isFilterActive(config: TableHeaderCellFilterOptionsConfig): boolean {
    if (isMultiSelectFilter(config)) {
        return config.selectedValues.length > 0;
    }
    return !!config.selectedValue;
}

export interface TableHeaderCellProps {
    label: React.ReactNode;
    sort?: TableHeaderCellSortConfig;
    filterOptions?: TableHeaderCellFilterOptionsConfig;
    filterContent?: (props: { onClose: () => void }) => React.ReactNode;
    filterActive?: boolean;
    /** Optional className for the dropdown when using filterContent */
    filterDropdownClassName?: string;
    isSearchable?: boolean;
}

export const TableHeaderCell = ({
    label,
    sort,
    filterOptions,
    filterContent,
    filterActive = false,
    filterDropdownClassName,
    isSearchable = false,
}: TableHeaderCellProps) => {
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [searchFilterValue, setSearchFilterValue] = useState('');
    const [filteredOptions, setFilteredOptions] = useState<TableHeaderFilterOption[]>([]);
    const filterButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (filterOptions?.options) {
            setFilteredOptions(filterOptions.options);
        }
    }, [filterOptions]);

    const handleFilterToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsFilterOpen((prev) => !prev);
    };

    const handleFilterClose = () => {
        setIsFilterOpen(false);
        setSearchFilterValue('');
    };

    const handleSearchFilterChange = (value: string) => {
        setSearchFilterValue(value);
    };

    const hasFilter = !!filterOptions || !!filterContent;
    const showFilterActive = filterActive || (filterOptions ? isFilterActive(filterOptions) : false);
    const isMultiSelect = filterOptions ? isMultiSelectFilter(filterOptions) : false;

    const labelContent = sort ? (
        <div
            className={styles.headerLabelSortable}
            onClick={(e) => {
                e.stopPropagation();
                sort.onSort();
            }}
            role='button'
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    sort.onSort();
                }
            }}
        >
            {label}
            <span className={styles.headerSortIcon} aria-label={sort.isSorted ? `Sort ${sort.direction}` : 'Sort'}>
                {sort.isSorted && sort.direction === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
        </div>
    ) : (
        <span className={styles.headerLabel}>{label}</span>
    );

    return (
        <div className={styles.headerCell}>
            {labelContent}
            {hasFilter && (
                <div className={styles.headerFilterRow}>
                    <Button
                        className={showFilterActive ? styles.headerFilterActive : ''}
                        iconOnly
                        size='sm'
                        variant='ghost'
                        icon={ListFilter}
                        onClick={handleFilterToggle}
                        ref={filterButtonRef}
                    />
                    <Dropdown
                        isOpen={isFilterOpen}
                        onClose={handleFilterClose}
                        toggleRef={filterButtonRef}
                        className={filterOptions ? styles.headerFilterDropdown : filterDropdownClassName}
                        selected={
                            filterOptions && !isMultiSelectFilter(filterOptions)
                                ? filterOptions.selectedValue
                                : undefined
                        }
                        selectedValues={
                            filterOptions && isMultiSelectFilter(filterOptions)
                                ? filterOptions.selectedValues
                                : undefined
                        }
                        searchPlaceholder={isSearchable ? 'Search statuses...' : undefined}
                        searchValue={searchFilterValue}
                        onSearchChange={handleSearchFilterChange}
                    >
                        {filterOptions
                            ? groupOptionsByCategory(
                                  filteredOptions.filter((option) =>
                                      option.label.toLowerCase().includes(searchFilterValue.toLowerCase())
                                  )
                              ).map(({ category, options: groupOptions }) => (
                                  <React.Fragment key={category ?? '__none__'}>
                                      {category && (
                                          <div className={styles.filterCategoryLabel} role='presentation'>
                                              {category}
                                          </div>
                                      )}
                                      {groupOptions.map((option) => (
                                          <DropdownItem
                                              key={option.value || 'all'}
                                              label={option.label}
                                              value={option.value}
                                              isSelected={isFilterOptionSelected(filterOptions, option.value)}
                                              selectionMode={isMultiSelect ? 'multi' : 'single'}
                                              onClick={(value) => {
                                                  handleFilterOptionChange(filterOptions, value as string);
                                                  if (!isMultiSelect) {
                                                      handleFilterClose();
                                                  }
                                              }}
                                          />
                                      ))}
                                  </React.Fragment>
                              ))
                            : filterContent
                              ? filterContent({ onClose: handleFilterClose })
                              : null}
                    </Dropdown>
                </div>
            )}
        </div>
    );
};
